import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHeart} from "@fortawesome/free-solid-svg-icons";
import {httpsCallable} from "@firebase/functions";
import {functions} from "../../../firebase/FirebaseProvider";
import {User} from "../../types/Types";
import {LIKE_COLOUR_LIKED, LIKE_COLOUR_UNLIKED} from "../../util/Util";

interface Props {
    entityId: string;
    likeType: 'PHOTO' | 'COMMENT' | 'LOCATION';
    initialLikeCount: number;
    isLiked: boolean;
    currentUser: User | undefined;
    locationId: string;
    locationName: string;
    onLikeCountChange?: (entityId: string, adjustment: number) => void;
    marginRight?: string;
    unlikedColour?: string;
    buttonClassName?: string;
}

function LikeButton(props: Props) {
    const [isLiked, setIsLiked] = useState<boolean>(props.isLiked);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [likeCountAdjustment, setLikeCountAdjustment] = useState<number>(0);

    // Calculate display count: use parent's count + our local adjustment
    const displayLikeCount = props.onLikeCountChange 
        ? props.initialLikeCount  // Parent manages the count
        : props.initialLikeCount + likeCountAdjustment;  // We manage our own adjustment

    // Update isLiked when props.isLiked changes
    useEffect(() => {
        setIsLiked(props.isLiked);
    }, [props.isLiked]);

    const handleLikeClick = async () => {
        if (isLoading || !props.currentUser) {
            return;
        }

        setIsLoading(true);
        const newLikedState = !isLiked;
        const adjustment = newLikedState ? 1 : -1;

        // Optimistically update the UI
        setIsLiked(newLikedState);
        
        // If we have a callback, let the parent handle the count
        // Otherwise, update our local adjustment
        if (props.onLikeCountChange) {
            props.onLikeCountChange(props.entityId, adjustment);
        } else {
            setLikeCountAdjustment(prev => prev + adjustment);
        }

        try {
            const toggleLike = httpsCallable(functions, 'toggleLike');
            await toggleLike({
                likeType: props.likeType,
                likeValue: newLikedState,
                entityId: props.entityId,
                locationId: props.locationId,
                locationName: props.locationName
            });
        } catch (error) {
            console.error('Error toggling like:', error);
            // Revert the UI changes on error
            setIsLiked(!newLikedState);
            
            if (props.onLikeCountChange) {
                props.onLikeCountChange(props.entityId, -adjustment);
            } else {
                setLikeCountAdjustment(prev => prev - adjustment);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (props.buttonClassName) {
        return (
            <button
                aria-label={isLiked ? "Unlike" : "Like"}
                className={`${props.buttonClassName} ${isLiked ? "location-v2-heart-button-active" : ""}`}
                disabled={isLoading || !props.currentUser}
                onClick={handleLikeClick}
                type="button"
            >
                <FontAwesomeIcon
                    icon={faHeart}
                    className={isLiked ? "" : "far"}
                    style={{
                        color: isLiked ? LIKE_COLOUR_LIKED : props.unlikedColour || LIKE_COLOUR_UNLIKED,
                        fontWeight: isLiked ? "900" : "400",
                    }}
                />
                {displayLikeCount > 0 && (
                    <span>{displayLikeCount}</span>
                )}
            </button>
        );
    }

    return (
        <div
            style={{
                float: "right",
                position: "relative",
                display: "inline-block",
                marginRight: props.marginRight || "0em",
                marginTop: "1px",
            }}
        >
            <FontAwesomeIcon
                icon={faHeart}
                onClick={handleLikeClick}
                className={isLiked ? "" : "far"}
                style={{
                    cursor: "pointer",
                    color: isLiked ? LIKE_COLOUR_LIKED : props.unlikedColour || LIKE_COLOUR_UNLIKED,
                    fontSize: "1.3em",
                    fontWeight: isLiked ? "900" : "400",
                }}
            />
            {displayLikeCount > 0 && (
                <span
                    style={{
                        position: "absolute",
                        top: "40%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: "0.6em",
                        fontWeight: "900",
                        pointerEvents: "none",
                    }}
                >
                    {displayLikeCount}
                </span>
            )}
        </div>
    );
}

export default LikeButton;
